import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpFromLine, CalendarDays, ChevronLeft, ChevronRight, Landmark } from "lucide-react";
import { calendarApi, type CalendarDay } from "../lib/api";
import { formatCurrency, formatSignedCurrency } from "../lib/format";
import { useI18n } from "../lib/i18n";

/** "2026-09" -> shifted by n months, staying in YYYY-MM form. */
function shiftMonth(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  // Date handles the year rollover; month is 0-indexed here.
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function CalendarPage() {
  const { t, lang } = useI18n();
  const [month, setMonth] = useState(currentMonth());

  const q = useQuery({
    queryKey: ["calendar", month],
    queryFn: () => calendarApi.show(month),
  });

  const data = q.data;

  const monthLabel = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(
      lang === "fa" ? "fa-IR" : lang === "ar" ? "ar" : "en",
      { month: "long", year: "numeric", timeZone: "UTC" },
    );
  }, [month, lang]);

  // Weekday headers, Sunday-first to match the server's first_weekday.
  const weekdays = useMemo(() => {
    const locale = lang === "fa" ? "fa-IR" : lang === "ar" ? "ar" : "en";
    return Array.from({ length: 7 }, (_, i) =>
      new Date(Date.UTC(2024, 8, 1 + i)).toLocaleDateString(locale, { weekday: "short", timeZone: "UTC" }),
    );
  }, [lang]);

  const totals = data?.totals;

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.calendar")}</p>
          <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-accent" />
            {t("calendar.title")}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
            className="btn-ghost"
            aria-label={t("calendar.prevMonth")}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="min-w-[10rem] text-center font-semibold text-parchment">{monthLabel}</span>
          <button
            type="button"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            className="btn-ghost"
            aria-label={t("calendar.nextMonth")}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setMonth(currentMonth())} className="btn-ghost">
            {t("calendar.today")}
          </button>
        </div>
      </header>

      {/* Month summary */}
      <section className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label={t("calendar.netPl")}
          value={totals ? formatSignedCurrency(totals.net_pl) : "—"}
          accent={
            !totals || totals.net_pl === 0
              ? "text-parchment"
              : totals.net_pl > 0
                ? "text-pnl-pos"
                : "text-pnl-neg"
          }
          sub={totals ? t("calendar.tradesCount", { count: totals.trades }) : undefined}
        />
        <SummaryCard
          label={t("calendar.greenRed")}
          value={totals ? `${totals.green_days} / ${totals.red_days}` : "—"}
          sub={t("calendar.greenRedSub")}
        />
        <SummaryCard
          label={t("calendar.deposits")}
          value={totals ? formatCurrency(totals.deposits) : "—"}
          accent="text-pnl-pos"
        />
        <SummaryCard
          label={t("calendar.withdrawals")}
          value={totals ? formatCurrency(totals.withdrawals) : "—"}
          accent="text-pnl-neg"
          /* Banked money is not spent money: it is reported under the figure
             rather than folded into it. Hidden when nothing was saved. */
          sub={
            totals && totals.savings > 0
              ? t("wallet.savedTotal", { amount: formatCurrency(totals.savings) })
              : undefined
          }
        />
      </section>

      <section className="glass p-3 sm:p-5">
        {q.isLoading && <p className="text-parchment-muted text-sm py-8 text-center">{t("common.loading")}</p>}
        {q.isError && (
          <p className="text-pnl-neg text-sm py-8 text-center">{(q.error as Error)?.message}</p>
        )}

        {data && (
          <>
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
              {weekdays.map((w) => (
                <div key={w} className="text-center text-[10px] sm:text-xs uppercase tracking-wider text-parchment-muted py-1">
                  {w}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {/* Leading blanks so day 1 sits under its real weekday. */}
              {Array.from({ length: data.first_weekday }).map((_, i) => (
                <div key={`pad-${i}`} aria-hidden="true" />
              ))}
              {data.days.map((d) => (
                <DayCell key={d.date} day={d} />
              ))}
            </div>

            <p className="text-[11px] text-parchment-muted mt-4 text-center">
              {t("calendar.tzNote", { tz: data.timezone })}
            </p>
          </>
        )}
      </section>
    </>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="glass p-4">
      <p className="label mb-1">{label}</p>
      <p className={`text-xl font-bold tabular ${accent ?? "text-parchment"}`}>{value}</p>
      {sub && <p className="text-[11px] text-parchment-muted mt-1">{sub}</p>}
    </div>
  );
}

function DayCell({ day }: { day: CalendarDay }) {
  const { t } = useI18n();
  const dayNum = Number(day.date.slice(-2));
  const hasTrades = day.trades > 0;
  const hasCash = day.deposits > 0 || day.withdrawals > 0 || day.savings > 0;
  const isToday = day.date === new Date().toISOString().slice(0, 10);

  // A day is green/red only on TRADING result; cash movements are not P/L and
  // must not colour the day, or a deposit would read as a winning session.
  const tone = !hasTrades
    ? "border-glass"
    : day.net_pl > 0
      ? "border-emerald-500/30 bg-emerald-500/[0.07]"
      : day.net_pl < 0
        ? "border-rose-500/30 bg-rose-500/[0.07]"
        : "border-glass";

  const plCls = day.net_pl > 0 ? "text-pnl-pos" : day.net_pl < 0 ? "text-pnl-neg" : "text-parchment-muted";

  return (
    <div
      className={`rounded-lg border p-1.5 sm:p-2 min-h-[4.5rem] sm:min-h-[5.5rem] flex flex-col gap-1 ${tone} ${
        isToday ? "ring-1 ring-accent" : ""
      }`}
      title={
        hasTrades
          ? t("calendar.dayTitle", {
              date: day.date,
              wins: day.wins,
              losses: day.losses,
            })
          : day.date
      }
    >
      <span className={`text-[11px] sm:text-xs tabular ${isToday ? "text-accent font-bold" : "text-parchment-muted"}`}>
        {dayNum}
      </span>

      {hasTrades && (
        <span className={`text-[11px] sm:text-sm font-semibold tabular leading-tight ${plCls}`}>
          {formatSignedCurrency(day.net_pl)}
        </span>
      )}

      <div className="mt-auto flex flex-col gap-0.5">
        {hasTrades && (
          <span className="text-[9px] sm:text-[10px] text-parchment-muted tabular">
            {t("calendar.nTrades", { count: day.trades })}
          </span>
        )}
        {hasCash && (
          <span className="flex items-center gap-1 flex-wrap">
            {day.deposits > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] text-pnl-pos tabular">
                <ArrowDownToLine className="w-2.5 h-2.5 shrink-0" />
                {formatCurrency(day.deposits)}
              </span>
            )}
            {day.withdrawals > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] text-pnl-neg tabular">
                <ArrowUpFromLine className="w-2.5 h-2.5 shrink-0" />
                {formatCurrency(day.withdrawals)}
              </span>
            )}
            {day.savings > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] text-accent tabular"
                title={t("wallet.savedTotal", { amount: formatCurrency(day.savings) })}
              >
                <Landmark className="w-2.5 h-2.5 shrink-0" />
                {formatCurrency(day.savings)}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
